/**
 * Shop Search Bar Web Component
 * ------------------------------
 * A custom search input with a pill-shaped container and search icon.
 *
 * Attributes:
 * - value: The current search query (default: "")
 *
 * Events dispatched:
 * - search-input: { detail: { value: string } } - Fires on each input change
 *
 * Visual Design:
 * - Pill-shaped container with border-radius: 9999px
 * - Search icon on the left (20px size, magnifying glass)
 * - 6px gap between icon and input text
 * - 16px horizontal padding, 10px vertical padding
 * - Input text: 20px font size
 * - Typed text color: #141414 (dark)
 * - Transparent background with backdrop blur effect: backdrop-filter: blur(30px)
 * - No border or minimal border styling
 *
 * Example:
 *   <shop-search-bar
 *     value="search query">
 *   </shop-search-bar>
 */

import { dispatchTrack } from "../../../analytics/shop-analytics-track.js";
import { suggestionText } from "../../../modules/search_suggestion_text.js";
import {
  suggestionCardSize,
  suggestionCardWrapCss,
  watchSuggestionCardSize,
} from "../../../modules/search_suggestion_card.js";

const ShopSearchBar = (function () {
  const TAG_NAME = "shop-search-bar";

  class ShopSearchBar extends HTMLElement {
    static get observedAttributes() {
      return ["value"];
    }

    constructor() {
      super();
      this._results = [];
      this._totalCount = 0;
      this._showResults = false;
      this._debounceTimer = null;
      this._selectedIndex = -1;
      this._fetchController = null;
    }

    connectedCallback() {
      if (!this._initialized) {
        this.render();
        this._initialized = true;
        this._attachEventListeners();
      }
    }

    disconnectedCallback() {
      this._detachEventListeners();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue || !this._initialized) return;

      // Update specific attributes without full re-render for performance
      if (name === "value") {
        const input = this.querySelector("input");
        if (input) {
          input.value = newValue || "";
        }
      }
    }

    _attachEventListeners() {
      const input = this.querySelector("input");
      const container = this.querySelector(".shop-search-bar__container");

      if (input) {
        input.removeEventListener("input", this._onInput);
        input.addEventListener(
          "input",
          (this._onInput = this._handleInput.bind(this)),
        );

        input.removeEventListener("keydown", this._onKeydown);
        input.addEventListener(
          "keydown",
          (this._onKeydown = this._handleKeydown.bind(this)),
        );

        // Handle focus/blur for cursor visibility
        input.removeEventListener("focus", this._onFocus);
        input.addEventListener(
          "focus",
          (this._onFocus = () => {
            container?.classList.add("shop-search-bar__container--focused");
            if (input.value.length > 0) {
              input.setSelectionRange(input.value.length, input.value.length);
            }
            if (this._results.length > 0 && input.value.trim().length >= 2) {
              this._renderAutocomplete();
            }
          }),
        );

        input.removeEventListener("blur", this._onBlur);
        input.addEventListener(
          "blur",
          (this._onBlur = () => {
            container?.classList.remove("shop-search-bar__container--focused");
          }),
        );
      }

      // Cards already showing follow the screen into or out of the compact size.
      this._unwatchCardSize?.();
      this._unwatchCardSize = watchSuggestionCardSize((size) => {
        this.querySelectorAll("fragno-offer-card").forEach((card) =>
          card.setAttribute("size", size),
        );
      });

      // Hide autocomplete when clicking outside
      document.removeEventListener("click", this._onDocumentClick);
      document.addEventListener(
        "click",
        (this._onDocumentClick = this._handleDocumentClick.bind(this)),
      );
    }

    _detachEventListeners() {
      this._unwatchCardSize?.();
      this._unwatchCardSize = null;

      const input = this.querySelector("input");
      if (input) {
        if (this._onInput) input.removeEventListener("input", this._onInput);
        if (this._onKeydown)
          input.removeEventListener("keydown", this._onKeydown);
        if (this._onFocus) input.removeEventListener("focus", this._onFocus);
        if (this._onBlur) input.removeEventListener("blur", this._onBlur);
      }

      if (this._onDocumentClick) {
        document.removeEventListener("click", this._onDocumentClick);
      }

      const dropdown = this.querySelector(
        ".shop-search-bar__autocomplete-dropdown",
      );
      if (dropdown) {
        if (this._onAutocompleteMouseEnter) {
          dropdown.removeEventListener(
            "mouseenter",
            this._onAutocompleteMouseEnter,
          );
        }
        if (this._onAutocompleteClick) {
          dropdown.removeEventListener("click", this._onAutocompleteClick);
        }
      }

      clearTimeout(this._debounceTimer);
      this._fetchController?.abort();
    }

    _handleDocumentClick(e) {
      // Hide autocomplete if clicking outside the component
      if (!e.composedPath().includes(this)) {
        this._hideAutocomplete();
      }
    }

    _handleInput(e) {
      const input = e.target;
      const value = input.value;
      const container = this.querySelector(".shop-search-bar__container");

      // Update cursor visibility based on input value
      if (container) {
        if (value.length > 0) {
          container.classList.add("shop-search-bar__container--has-value");
        } else {
          container.classList.remove("shop-search-bar__container--has-value");
        }
      }

      // Dispatch custom input event with value in detail
      this.dispatchEvent(
        new CustomEvent("search-input", {
          bubbles: true,
          composed: true,
          detail: { value },
        }),
      );

      // Trigger autocomplete with debouncing
      this._debounceAutocomplete(value);
    }

    _debounceAutocomplete(value) {
      clearTimeout(this._debounceTimer);

      if (!value || value.trim().length < 2) {
        this._hideAutocomplete();
        return;
      }

      this._debounceTimer = setTimeout(() => {
        this._fetchAutocomplete(value);
      }, 300);
    }

    async _fetchAutocomplete(query) {
      clearTimeout(this._debounceTimer);
      this._fetchController?.abort();
      this._selectedIndex = -1;
      this._fetchController = new AbortController();
      try {
        const response = await fetch(
          `/stores.json?q=${encodeURIComponent(query)}`,
          {
            method: "GET",
            headers: { Accept: "application/json" },
            credentials: "include",
            signal: this._fetchController.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Autocomplete fetch failed: ${response.status}`);
        }

        const data = await response.json();
        let stores = data.results || [];

        if (stores.length === 0) {
          this._hideAutocomplete();
          return;
        }

        this._totalCount = data.total_count || stores.length;
        this._results = stores.slice(0, 5);
        this._renderAutocomplete();
      } catch (error) {
        if (error.name !== "AbortError") {
          this._hideAutocomplete();
        }
      }
    }

    _renderAutocomplete() {
      const dropdown = this.querySelector(
        ".shop-search-bar__autocomplete-dropdown",
      );
      if (!dropdown) {
        return;
      }

      if (this._results.length === 0) {
        this._hideAutocomplete();
        return;
      }
      dropdown.replaceChildren();
      const list = document.createElement("div");
      list.className = "shop-search-bar__results-list";
      this._results.forEach((store, index) => {
        const merchantUrl = store.url || "#";
        // CL-23: the subtitle is the member's top offer, or the cash back rate when there is none.
        const { cashBack, subtitle } = suggestionText(store);
        const card = document.createElement("fragno-offer-card");
        card.id = `shop-search-result-${index}`;
        card.setAttribute("role", "option");
        card.setAttribute("data-index", String(index));
        if (cashBack) card.setAttribute("store-text", store.name || "");
        card.setAttribute("title", store.name || "");
        if (subtitle) card.setAttribute("subtitle", subtitle);
        card.setAttribute("brand-logo", store.logo_attachment || "");
        card.setAttribute("size", suggestionCardSize());
        card.setAttribute("card-style", "outline");
        card.setAttribute("card-href", merchantUrl);
        list.appendChild(card);
      });
      dropdown.appendChild(list);

      dropdown.classList.add("shop-search-bar__autocomplete-dropdown--show");
      dropdown.setAttribute("role", "listbox");
      this._showResults = true;
      const input = this.querySelector("input");
      if (input) input.setAttribute("aria-expanded", "true");
      this._attachAutocompleteListeners();

      // Delay announcement so VoiceOver detects it as a separate DOM mutation
      const srStatus = this.querySelector(".shop-search-bar__sr-status");
      if (srStatus) {
        srStatus.textContent = "";
        setTimeout(() => {
          srStatus.textContent = `Results (${this._totalCount})`;
        }, 100);
      }
    }

    _hideAutocomplete() {
      const dropdown = this.querySelector(
        ".shop-search-bar__autocomplete-dropdown",
      );
      if (dropdown) {
        dropdown.classList.remove(
          "shop-search-bar__autocomplete-dropdown--show",
        );
        dropdown.innerHTML = "";
        dropdown.removeAttribute("role");
      }
      this._showResults = false;
      this._selectedIndex = -1;
      const input = this.querySelector("input");
      if (input) input.setAttribute("aria-expanded", "false");
      const srStatus = this.querySelector(".shop-search-bar__sr-status");
      if (srStatus) srStatus.textContent = "";
    }

    _attachAutocompleteListeners() {
      const dropdown = this.querySelector(
        ".shop-search-bar__autocomplete-dropdown",
      );
      if (!dropdown) return;

      dropdown.removeEventListener(
        "mouseenter",
        this._onAutocompleteMouseEnter,
      );
      dropdown.addEventListener(
        "mouseenter",
        (this._onAutocompleteMouseEnter = (e) => {
          if (e.target.closest("fragno-offer-card")) {
            this._selectedIndex = -1;
            this._updateSelectedItem();
          }
        }),
      );

      // Track clicks on autocomplete suggestion cards
      dropdown.removeEventListener("click", this._onAutocompleteClick);
      dropdown.addEventListener(
        "click",
        (this._onAutocompleteClick = (e) => {
          const card = e.target.closest("fragno-offer-card");
          if (!card) return;
          const index = parseInt(card.dataset.index, 10);
          const store = this._results[index];
          if (!store) return;
          const input = this.querySelector("input");
          this._trackSearch("search_suggestion_click", {
            store_id: store.id,
            store_slug: store.slug,
            label: input?.value?.trim() || "",
          });
        }),
      );
    }

    _updateSelectedItem() {
      const items = this.querySelectorAll("fragno-offer-card");
      items?.forEach((item, index) => {
        if (index === this._selectedIndex) {
          item.classList.add("shop-search-bar__autocomplete-card--selected");
        } else {
          item.classList.remove("shop-search-bar__autocomplete-card--selected");
        }
      });

      const input = this.querySelector("input");
      if (input) {
        if (this._selectedIndex >= 0) {
          input.setAttribute(
            "aria-activedescendant",
            `shop-search-result-${this._selectedIndex}`,
          );
        } else {
          input.removeAttribute("aria-activedescendant");
        }
      }
    }

    _handleKeydown(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        if (this._showResults) {
          this._hideAutocomplete();
        } else {
          this.dispatchEvent(
            new CustomEvent("search-close", { bubbles: true, composed: true }),
          );
        }
        return;
      }

      if (e.key === "Enter") {
        if (this._selectedIndex >= 0 && this._results[this._selectedIndex]) {
          e.preventDefault();
          const store = this._results[this._selectedIndex];
          if (store.url) {
            const input = this.querySelector("input");
            this._trackSearch("search_suggestion_click", {
              store_id: store.id,
              store_slug: store.slug,
              label: input?.value?.trim() || "",
            });
            this._navigate(store.url);
          }
        } else {
          const input = this.querySelector("input");
          const query = input?.value?.trim();
          if (query) {
            e.preventDefault();
            clearTimeout(this._debounceTimer);
            this._fetchController?.abort();
            this._trackSearch("search_submit", { label: query });
            this._navigate(`/offers?q=${encodeURIComponent(query)}`);
          }
        }
        return;
      }

      if (!this._showResults) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          this._selectedIndex = Math.min(
            this._selectedIndex + 1,
            this._results.length - 1,
          );
          this._updateSelectedItem();
          break;
        case "ArrowUp":
          e.preventDefault();
          this._selectedIndex = Math.max(this._selectedIndex - 1, -1);
          this._updateSelectedItem();
          break;
      }
    }

    render() {
      const value = this.getAttribute("value") || "";

      const template = document.createElement("template");
      template.innerHTML = `
        <style>
          shop-search-bar {
            display: block;
            width: 100%;
            background: var(--colors-white);
            padding-block: var(--spacing-xl) var(--spacing-3xl);
          }

          /* Horizontal padding mirrors the global nav inner padding
             (--spacing-8xl on desktop, --spacing-4xl on tablet/mobile) so the
             search icon aligns with the ID.me logo above. */
          .shop-search-bar__wrapper {
            width: 100%;
            max-width: 90rem;
            margin-inline: auto;
            padding-inline: var(--spacing-8xl);
            background: transparent;
            display: flex;
            flex-direction: column;
            gap: var(--spacing-3xl);
            box-sizing: border-box;
          }

          .shop-search-bar__search-section {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-xl);
          }

          .shop-search-bar__search-icon {
            flex-shrink: 0;
            color: var(--colors-gray-500);
          }

          .shop-search-bar__container {
            display: flex;
            align-items: center;
            gap: var(--spacing-sm);
            padding: 0.625rem 0;
            background: transparent;
            border: none;
            position: relative;
          }

          .shop-search-bar__cursor {
            width: 1px;
            height: 2.375rem;
            background: var(--colors-gray-700);
            flex-shrink: 0;
            animation: blink 1s step-end infinite;
          }

          @keyframes blink {
            0%, 50% { opacity: 1; }
            50.01%, 100% { opacity: 0; }
          }

          .shop-search-bar__container--has-value .shop-search-bar__cursor,
          .shop-search-bar__container--focused .shop-search-bar__cursor {
            visibility: hidden;
          }

          input {
            flex: 1;
            font-family: var(--font-family-body);
            font-size: var(--font-size-text-xl);
            font-weight: var(--font-weight-medium);
            line-height: var(--line-height-text-xl);
            letter-spacing: 0;
            background: transparent;
            border: none;
            outline: none;
            color: var(--colors-gray-900);
            padding: 0;
            margin: 0;
          }

          input::placeholder {
            color: var(--colors-gray-500);
          }

          input:focus {
            outline: none;
          }

          @media (max-width: 1023px) {
            .shop-search-bar__wrapper {
              padding-inline: var(--spacing-4xl);
            }

            input {
              font-size: var(--font-size-text-md);
              line-height: var(--line-height-text-md);
              letter-spacing: -0.005rem;
            }

            .shop-search-bar__cursor {
              height: 2rem;
            }
          }

          /* Autocomplete results */
          .shop-search-bar__autocomplete-dropdown {
            display: none;
            flex-direction: column;
            padding-bottom: var(--spacing-6xl);
          }

          .shop-search-bar__autocomplete-dropdown--show {
            display: flex;
          }

          .shop-search-bar__results-list {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-sm);
            width: 28.625rem;
          }

          @media (max-width: 1023px) {
            .shop-search-bar__results-list {
              width: 22.375rem;
            }
          }

          @media (max-width: 767px) {
            .shop-search-bar__results-list {
              width: 100%;
            }

            .shop-search-bar__results-list fragno-offer-card {
              --offer-card-sm-width: 100%;
              --offer-card-sm-min-width: 0px;
              --offer-card-sm-max-width: 100%;
            }
          }

          .shop-search-bar__results-list fragno-offer-card {
            width: 100%;
            min-width: 0;
            max-width: 100%;
          }

          .shop-search-bar__card-link {
            text-decoration: none;
            color: inherit;
            display: block;
            outline: none;
          }

          .shop-search-bar__card-link:focus-visible {
            outline: 0.125rem solid var(--colors-focus-ring);
            outline-offset: 0.125rem;
            border-radius: var(--radius-md, 0.5rem);
          }

          .shop-search-bar__autocomplete-card--selected {
            outline: 0.125rem solid var(--colors-focus-ring);
            outline-offset: 0.125rem;
          }

          .shop-search-bar__sr-status {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
          }
        </style>

        <div class="shop-search-bar__wrapper">
          <div class="shop-search-bar__search-section">
            <div class="shop-search-bar__container">
              <svg class="shop-search-bar__search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <div class="shop-search-bar__cursor" aria-hidden="true"></div>
              <input
                id="shop-search-input"
                type="text"
                autocomplete="off"
                aria-label="Search"
                aria-autocomplete="list"
                aria-expanded="false"
                aria-controls="shop-search-bar-results"
              />
            </div>
          </div>

          <div class="shop-search-bar__autocomplete-dropdown" id="shop-search-bar-results">
          </div>

          <div class="shop-search-bar__sr-status" role="status" aria-live="polite" aria-atomic="true"></div>
        </div>
      `;

      this.innerHTML = "";
      // Text wraps instead of truncating (see search_suggestion_card.js). Added as its own
      // <style> through textContent so the template above stays a static string.
      const cardStyle = document.createElement("style");
      cardStyle.textContent = suggestionCardWrapCss(
        ".shop-search-bar__results-list",
      );
      template.content.appendChild(cardStyle);

      this.appendChild(template.content.cloneNode(true));
      const input = this.querySelector("input");
      if (input) input.value = value;
    }

    _trackSearch(eventName, params) {
      dispatchTrack(this, eventName, params);
    }

    _navigate(url) {
      if (!url) return;
      window.location.href = url;
    }
  }

  if (!customElements.get(TAG_NAME)) {
    customElements.define(TAG_NAME, ShopSearchBar);
  }

  return ShopSearchBar;
})();

export { ShopSearchBar };
